import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { useFormik } from 'formik'

import AddPagesForm from '../../../AddPagesForm'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { formatOptionsList } from '../../../../../../features/formatters'

import { addAccessibleSocialPages } from '../../../../../../modules/actions/socialAccounts'
import { selectedPartnershipIdSelector } from '../../../../../../modules/selectors/partnerships'
import { accessibleSocialPagesSelector } from '../../../../../../modules/selectors/socialAccounts'
import { tikTokIdentitiesSelector } from '../../../../../../modules/selectors/tikTokConstants'

import { initialValues, PAGE_ID } from '../../../AddPagesForm/fields'
import { TIKTOK_PROVIDER_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { identityTypes } from '../../../../../../forms/Tiktok/AdForms/AdTikTokCreate/AdTikTokCreateForm/fields'

const AddTikTokIdentityForm = () => {
  const dispatch = useDispatch()

  const tikTokIdentities = useSelector(tikTokIdentitiesSelector)
  const selectedPartnershipId = useSelector(selectedPartnershipIdSelector)
  const accessibleSocialPages = useSelector(accessibleSocialPagesSelector)

  const availableTikTokIdentities = useMemo(() => {
    // use only identity_type: 'BC_AUTH_TT'
    return tikTokIdentities.filter(({ identity_type }) => identity_type === identityTypes.BC_AUTH_TT)
  }, [tikTokIdentities])

  // don't show already added pages. Filter out pages which exist in accessibleSocialPages list
  const availablePages = useMemo(() => {
    return availableTikTokIdentities.filter(
      ({ identity_id }) => !accessibleSocialPages.find(({ external_id }) => external_id === identity_id)
    )
  }, [availableTikTokIdentities, accessibleSocialPages])

  const formattedTikTokPages = useMemo(() => {
    return formatOptionsList({
      list: availablePages,
      valueName: 'identity_id',
      labelName: 'display_name',
      imgName: 'profile_image'
    })
  }, [availablePages])

  const onSubmit = useCallback(
    values => {
      const newTikTokPage = availableTikTokIdentities.find(({ identity_id }) => values[PAGE_ID] === identity_id)

      dispatch(
        addAccessibleSocialPages({
          ...newTikTokPage,
          external_id: newTikTokPage.identity_id,
          partnership: selectedPartnershipId,
          platform: TIKTOK_PROVIDER_PLATFORM,
          name: newTikTokPage.display_name,
          thumbnail: newTikTokPage.profile_image
        })
      )
    },
    [dispatch, selectedPartnershipId, availableTikTokIdentities]
  )

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      [PAGE_ID]: Yup.string().required('Please select TikTok identity')
    }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  return (
    <AddPagesForm formik={purifiedFormik} pagesOptions={formattedTikTokPages} provider={TIKTOK_PROVIDER_PLATFORM} />
  )
}

export default AddTikTokIdentityForm
